import React, { useMemo, useState, useEffect } from "react";
import { Grid, Card } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../../examples/Tables/DataTable";
import PropTypes from "prop-types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import NotisSearch from "components/STSearch/NotisSearch";
import { apiGetUUUPosted } from "api/notis";
import { apiGetAkaunDetailByNoAkaun } from "api/akaun";
// removed unused imports

function NotisTunggakanUUU() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState({ stage: "ketiga", zon: "", no_akaun: "" });
  // single selection for LIHAT
  const [selectedKey, setSelectedKey] = useState(null);
  // expanded detail row key and cache of details by no_akaun
  const [expandedKey, setExpandedKey] = useState(null);
  const [detailCache, setDetailCache] = useState({}); // { [no_akaun]: { loading, error, data } }

  // Fetch all mark_notis=2 and group by no_akaun; keep a representative no_borang (latest by tarikh_notis)
  const loadRows = async () => {
    setLoading(true); setError("");
    try {
      const res = await apiGetUUUPosted();
      const src = Array.isArray(res?.rows) ? res.rows : [];
      // Group by no_akaun
      const groups = new Map();
      for (const r of src) {
        const key = String(r.no_akaun);
        let g = groups.get(key);
        const list = [String(r.no_inbois)];
        if (!g) {
          g = {
            key,
            nama: r.nama || "-",
            zon: r.zon || "-",
            no_akaun: r.no_akaun,
            inbois_list: list,
            tempoh_notis_hari: r.tempoh_notis_hari ?? null,
            makluman_uuu: r.makluman_uuu === true,
            no_borang: r.no_borang || null,
            _latestTarikh: r.tarikh_notis ? new Date(r.tarikh_notis) : null,
          };
          groups.set(key, g);
        } else {
          g.inbois_list.push(...list);
          // keep max tempoh
          const existing = typeof g.tempoh_notis_hari === 'number' ? g.tempoh_notis_hari : -Infinity;
          const next = typeof r.tempoh_notis_hari === 'number' ? r.tempoh_notis_hari : -Infinity;
          if (next > existing) g.tempoh_notis_hari = r.tempoh_notis_hari;
          // if any makluman_uuu true, keep true
          g.makluman_uuu = g.makluman_uuu || (r.makluman_uuu === true);
          // track latest tarikh_notis to choose representative no_borang
          const currDate = r.tarikh_notis ? new Date(r.tarikh_notis) : null;
          if (currDate && (!g._latestTarikh || currDate > g._latestTarikh)) {
            g._latestTarikh = currDate;
            g.no_borang = r.no_borang || g.no_borang || null;
          }
          if (!g.no_borang && r.no_borang) {
            // fallback if we don't have any yet
            g.no_borang = r.no_borang;
          }
        }
      }
      const out = Array.from(groups.values()).map(({ _latestTarikh, ...rest }) => rest);
      setRows(out);
    } catch (e) {
      setError(e.message || 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); }, []);

  const ValueCell = ({ value }) => (
    <MDTypography variant="caption" color="text" fontWeight="medium">
      {value ?? "-"}
    </MDTypography>
  );
  ValueCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

  const columns = useMemo(() => [
    {
      Header: <div style={{ width: "100%" }} />,
      accessor: "__select__",
      align: "center",
      headerAlign: "center",
      width: "6%",
      disableSortBy: true,
      Cell: (cell) => {
        const key = cell.row?.original?.key;
        const checked = selectedKey === key;
        return (
          <input
            type="checkbox"
            checked={!!checked}
            onChange={(e) => { e.stopPropagation(); setSelectedKey(checked ? null : key); }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ cursor: 'pointer' }}
          />
        );
      },
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>NAMA</div>,
      accessor: "nama",
      align: "left",
      headerAlign: "center",
  Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>ZON</div>,
      accessor: "zon",
      align: "left",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>NO AKAUN</div>,
      accessor: "no_akaun",
      align: "center",
      headerAlign: "center",
      width: "15%",
      Cell: (cell) => <ValueCell value={cell.value} />,
    },
    {
      Header: <div style={{ textAlign: "center", width: "100%" }}>TEMPOH NOTIS</div>,
      accessor: "tempoh_notis",
      align: "center",
      headerAlign: "center",
      width: "18%",
      Cell: (cell) => {
        const row = cell.row?.original || {};
        let days = row?.tempoh_notis_hari ?? "-";
        const color = typeof days === "number"
          ? (days >= 14 ? "#c1121f" : days >= 8 ? "#d98a00" : "#2e7d32")
          : undefined;
        return (
          <div style={{ textAlign: "center" }}>
            <MDTypography variant="caption" fontWeight="medium" sx={{ color }}>
              {days}
            </MDTypography>
          </div>
        );
      },
    },
  ], [selectedKey]);

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const filtered = useMemo(() => {
    let out = rows;
    if (search.stage) out = out.filter((r) => (search.stage === 'ketiga')); // UUU focuses on Makluman UUU state
    if (search.zon) out = out.filter((r) => (r.zon || '').toLowerCase() === search.zon.toLowerCase());
    if (search.no_akaun) out = out.filter((r) => String(r.no_akaun).toLowerCase().includes(String(search.no_akaun).toLowerCase()));
    // Sort by nama
    return [...out].sort((a,b) => (a.nama || '').localeCompare((b.nama || ''), 'ms', { sensitivity: 'base' }));
  }, [rows, search]);

  // Clear selection if the selected row is no longer in the filtered list
  useEffect(() => {
    if (selectedKey && !filtered.some((r) => r.key === selectedKey)) {
      setSelectedKey(null);
    }
  }, [filtered, selectedKey]);

  const handleLihat = async () => {
    const row = filtered.find(r => r.key === selectedKey);
    if (!row) {
      window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih satu baris untuk LIHAT.' });
      return;
    }
    const no_borang = row?.no_borang;
    if (!no_borang) {
      window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Tiada no borang untuk fail PDF.' });
      return;
    }
    try {
      const url = `/api/notis/file-by-borang/${encodeURIComponent(no_borang)}`;
      const resp = await fetch(url, { headers: { Accept: 'application/pdf' } });
      if (!resp.ok) {
        throw new Error(`Gagal memuat PDF (status ${resp.status})`);
      }
      const blob = await resp.blob();
      const mime = blob.type || 'application/pdf';
      const pdfBlob = mime === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (e) {
      window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Gagal membuka fail.' });
    }
  };

  // Row click: only toggle which row is expanded; fetching handled by effect
  const handleRowClick = (original) => {
    const key = original?.key;
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  // Lazy-fetch detail when a row is expanded and not yet in cache
  useEffect(() => {
    if (!expandedKey) return;
    const row = filtered.find((r) => r.key === expandedKey);
    const no_akaun = row?.no_akaun;
    if (!no_akaun) return;
    let cancelled = false;
    // Initialize loading state if missing
    setDetailCache((prev) => {
      if (prev[no_akaun]) return prev;
      return { ...prev, [no_akaun]: { loading: true, error: null, data: null } };
    });
    // Only fetch if not present
    const hasEntry = !!detailCache[no_akaun];
    if (!hasEntry) {
      (async () => {
        try {
          const res = await apiGetAkaunDetailByNoAkaun(no_akaun);
          const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
          const rec = arr[0] || null;
          if (!cancelled) {
            setDetailCache((prev) => ({ ...prev, [no_akaun]: { loading: false, error: null, data: rec } }));
          }
        } catch (e) {
          if (!cancelled) {
            setDetailCache((prev) => ({ ...prev, [no_akaun]: { loading: false, error: e.message || 'Gagal memuat maklumat', data: null } }));
          }
        }
      })();
    }
    return () => { cancelled = true; };
  }, [expandedKey, filtered, detailCache]);

  const renderDetail = (original) => {
    const no_akaun = original?.no_akaun;
    const st = detailCache[no_akaun];
    const loading = st?.loading;
    const err = st?.error;
    const d = st?.data || {};
    const alamatHarta = [d.alamat1_harta, d.alamat2_harta, d.poskod_harta, d.bandar_harta, d.negeri_harta]
      .filter(Boolean)
      .join(", ");
    const alamatPelanggan = [
      d.alamat1_pelanggan || d.alamat1,
      d.alamat2_pelanggan || d.alamat2,
      d.poskod_pelanggan || d.poskod,
      d.bandar_pelanggan || d.bandar,
      d.negeri_pelanggan || d.negeri,
    ]
      .filter(Boolean)
      .join(", ");
    return (
      <MDBox p={2} sx={{ backgroundColor: "#fafafa", borderTop: "1px solid #eee" }}>
        {loading && <MDTypography variant="caption">Memuat maklumat...</MDTypography>}
        {!loading && err && (
          <MDTypography variant="caption" color="error">{err}</MDTypography>
        )}
        {!loading && !err && (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="button" fontWeight="bold">Nama</MDTypography>
              <MDTypography variant="caption" display="block">{d.nama || original.nama || '-'}</MDTypography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="button" fontWeight="bold">No Akaun</MDTypography>
              <MDTypography variant="caption" display="block">{no_akaun}</MDTypography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="button" fontWeight="bold">No Tel</MDTypography>
              <MDTypography variant="caption" display="block">{d.notel || d.no_tel || '-'}</MDTypography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="button" fontWeight="bold">Emel</MDTypography>
              <MDTypography variant="caption" display="block">{d.emel || '-'}</MDTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="button" fontWeight="bold">Alamat Harta</MDTypography>
              <MDTypography variant="caption" display="block">{alamatHarta || '-'}</MDTypography>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDTypography variant="button" fontWeight="bold">Alamat Pelanggan</MDTypography>
              <MDTypography variant="caption" display="block">{alamatPelanggan || '-'}</MDTypography>
            </Grid>
          </Grid>
        )}
      </MDBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.warning}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Notis Tunggakan
                </MDTypography>
              </MDBox>
              <MDBox pt={2}>
                <NotisSearch
                  search={search}
                  onChange={handleSearchChange}
                  loading={loading}
                  onLihat={handleLihat}
                  lihatDisabled={loading || !selectedKey}
                  forceUUU
                />
                <DataTable
                  table={{ columns, rows: filtered }}
                  showTotalEntries={false}
                  isSorted={false}
                  noEndBorder
                  entriesPerPage={false}
                  canSearch={false}
                  onRowClick={handleRowClick}
                  renderDetailRow={renderDetail}
                  expandedRowKey={expandedKey}
                  getRowKey={(o) => o?.key}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default NotisTunggakanUUU;
